import { type KeyboardEventHandler, type ReactNode, type Ref } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { Breakpoint, tokens } from '@treams/treams-ui';

import TalentReviewScrollPane from './TalentReviewScrollPane';
import { SIDEBAR_WIDTH } from './TalentReviewSplitLayout';

/**
 * The sidebar pane: a column with a fixed header (title, search) and a list
 * that scrolls on its own.
 */
const Sidebar = styled.div`
	display: flex;
	position: relative;
	flex: 0 0 ${SIDEBAR_WIDTH}px;
	flex-direction: column;
	width: ${SIDEBAR_WIDTH}px;
	min-height: 0;
	box-sizing: border-box;
	overflow: hidden;
	background: ${tokens.surface.secondary};
	border-left: 1px solid ${tokens.border.subtle};

	@media screen and (max-width: ${Breakpoint.Medium}) {
		flex-basis: auto;
		width: 100%;
		overflow: visible;
		border-left: none;
		border-top: 1px solid ${tokens.border.subtle};
	}
`;

const Header = styled(Box)`
	flex: 0 0 auto;
	scrollbar-gutter: stable;
`;

const List = styled(TalentReviewScrollPane)`
	flex: 1 1 auto;
	scrollbar-gutter: stable;
`;

const Fade = styled.div<{ $edge: 'top' | 'bottom'; $hidden?: boolean }>`
	z-index: 1;
	position: absolute;
	left: 0;
	right: 0;
	height: 50px;
	pointer-events: none;
	opacity: ${(props) => (props.$hidden ? 0 : 1)};
	transition: opacity 150ms ease;
	${(props) =>
		props.$edge === 'top'
			? `
		bottom: 0;
		transform: translateY(100%);
		background: linear-gradient(to bottom, ${tokens.surface.secondary}, transparent);
	`
			: `
		bottom: 0;
		background: linear-gradient(to top, ${tokens.surface.secondary}, transparent);
	`}
`;

/**
 * Sidebar chrome around the team list: a fixed header, a scrolling list, and
 * fades at the list edges.
 * @param props component props
 * @param props.header title, search and other controls above the list
 * @param props.listRef the scrolling list; used to reveal the open card
 * @param props.scrolledFromTop hides the top fade when the list is at the top
 * @param props.onListKeyDown arrow keys step through the list
 * @param props.children the people in the list
 */
export default function TalentReviewSidebarLayout(props: {
	header: ReactNode;
	listRef?: Ref<HTMLDivElement>;
	scrolledFromTop?: boolean;
	onListKeyDown?: KeyboardEventHandler<HTMLDivElement>;
	children: ReactNode;
}) {
	return (
		<Sidebar>
			<Header pt={3} px={3}>
				{props.header}
				{/* Hangs from the header's bottom edge over the list. */}
				<Fade $edge="top" $hidden={!props.scrolledFromTop} />
			</Header>
			<List ref={props.listRef} pt={3} px={3} pb={4} role="listbox" onKeyDown={props.onListKeyDown}>
				{props.children}
			</List>
			<Fade $edge="bottom" />
		</Sidebar>
	);
}
